import { type Amount, addAmounts, divideByNumber, multiplyByNumber } from '@orus.eu/amount'
import {
  isApplicationValidatedDimension,
  ProductQuoteV2Dimension,
  rcdaSelectedDimension,
  StringsWithDataEnumDimension,
  type TypeOfDimension,
} from '@orus.eu/dimensions'
import type { PaymentRecurrence, RcdaQuote } from '@orus.eu/quote'

export class RcdaQuoteDimension<NAME extends string, Quote extends RcdaQuote> extends ProductQuoteV2Dimension<
  NAME,
  Quote
> {}

export const rcdaQuoteDimension = new RcdaQuoteDimension({
  name: 'rcdaQuote',
  displayKeys: { name: 'dimension_name_rcda_quote' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: {
    lockedInQuoteFor: {
      client: rcdaSelectedDimension,
      partner: { and: [isApplicationValidatedDimension, rcdaSelectedDimension] },
      platform: { and: [isApplicationValidatedDimension, rcdaSelectedDimension] },
    },
  },
} as const)

export const rcdaQuoteExclusionDimension = new StringsWithDataEnumDimension({
  name: 'rcdaQuoteExclusion',
  displayKeys: { name: 'dimension_name_rcda_quote_exclusion' },
  entries: [
    ['insurance-history', { label: "Antécédents d'assurance" }],
    ['employees-count', { label: "Nombre d'employés" }],
    ['excluded-activity', { label: 'Activité exclue' }],
  ],
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export type RcdaQuoteExclusion = TypeOfDimension<typeof rcdaQuoteExclusionDimension>

export function getRcdaInstallmentFeePerYear(rcdaQuote: RcdaQuote): Amount | undefined {
  // Old installment fees were in monthlyFees.installment, it's now in the generic orusFees.installmentFee
  return rcdaQuote.monthlyFees?.installment ?? rcdaQuote.monthly.final.orusFees?.installmentFee?.premium.annualizedTotal
}

export function getRcdaInstallmentFeePerMonth(rcdaQuote: RcdaQuote): Amount | undefined {
  const installmentFeesPerYear = getRcdaInstallmentFeePerYear(rcdaQuote)

  return installmentFeesPerYear ? divideByNumber(installmentFeesPerYear, 12) : undefined
}

export function getRcdaEffectiveYearlyTotalPremium({
  rcdaQuote,
  paymentRecurrence,
}: {
  rcdaQuote: RcdaQuote
  paymentRecurrence: PaymentRecurrence
}): Amount {
  return paymentRecurrence === 'monthly'
    ? getRcdaCummulatedMonthlyTotalPremiumPerYear(rcdaQuote)
    : rcdaQuote.yearly.final.total.total
}

export function getRcdaCummulatedMonthlyTotalPremiumPerYear(rcdaQuote: RcdaQuote): Amount {
  return addAmounts(
    rcdaQuote.monthly.final.total.firstMonthTotal,
    multiplyByNumber(rcdaQuote.monthly.final.total.subsequentMonthsTotal, 11),
  )
}
